import { useState, useRef } from "react";
import { playMicStartCueA, playMicStopCueA } from "../../../mic-sounds/micCues";
import { useCrePermission } from "../../../Hooks/useCrePermission";
import { CRE_PERMISSION } from "../../../cre/crePermissions";

// Cap on the number of audio chunks we hold while waiting for the socket to
// be ready. Each chunk is a 4096-sample mono Int16 buffer at 16 kHz, so
// ~80 chunks ≈ 20 seconds. Generous safety net; in practice the queue
// flushes within a few hundred ms of click.
const MAX_BUFFERED_CHUNKS = 80;

const useAudioRecording = ({
  connectSocket,
  disconnectSocket,
  onError,
  language = "en-US",
  anathomy = "",
  onBeforeStart,
  onSessionStart,
  onSessionEnd,
  logPrefix = "mic",
}) => {
  const { requirePermission } = useCrePermission();
  const [isRecording, setIsRecording] = useState(false);
  const [processor, setProcessor] = useState(null);
  const [source, setSource] = useState(null);
  const [stream, setStream] = useState(null);
  const audioContextRef = useRef(null);
  const socketRef = useRef(null);

  // Audio is captured immediately on click and queued here until the speech
  // recognition socket is connected and `startAudio` has been emitted. This
  // prevents the first ~300–1300 ms of speech from being lost while the
  // socket handshakes and the API enters listening mode.
  const pendingAudioBuffersRef = useRef([]);
  const socketReadyRef = useRef(false);
  // Set true if `stopRecording` is invoked while `startRecording` is still
  // in flight. All async checkpoints honour this and bail out cleanly.
  const startCancelledRef = useRef(false);

  // Single choke point for every mic start (button, hotkey, command mode,
  // CRE mirror extension), so a locked CRE user cannot reach the recorder
  // through any surface. Returns whether recording actually started.
  const startRecording = async () => {
    if (!requirePermission(CRE_PERMISSION.BASIC)) return false;

    pendingAudioBuffersRef.current = [];
    socketReadyRef.current = false;
    startCancelledRef.current = false;

    const t0 = performance.now();
    const log = (label) =>
      console.log(
        `🎤 [${logPrefix}] ${label} (+${Math.round(performance.now() - t0)}ms)`,
      );

    log("click");

    let newStream = null;
    let newAudioContext = null;
    let newSource = null;
    let newProcessor = null;
    let connectedSocket = null;

    try {
      await onBeforeStart?.();
      onSessionStart?.();

      // Kick off mic capture and socket connect in PARALLEL so the user's
      // first words are captured into the in-memory buffer while the socket
      // is still handshaking.
      const mediaPromise = navigator.mediaDevices.getUserMedia({ audio: true });
      const socketPromise = connectSocket();

      // ----- Mic side: start capturing as soon as possible -----
      newStream = await mediaPromise;
      log("getUserMedia resolved");
      if (startCancelledRef.current) {
        newStream.getTracks().forEach((t) => t.stop());
        return false;
      }
      setStream(newStream);

      newAudioContext = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = newAudioContext;
      // Some browsers create AudioContext suspended (Chrome autoplay policy).
      if (newAudioContext.state === "suspended") {
        try {
          await newAudioContext.resume();
        } catch (_) {
          // best-effort
        }
      }
      log("AudioContext ready");

      newSource = newAudioContext.createMediaStreamSource(newStream);
      newProcessor = newAudioContext.createScriptProcessor(4096, 1, 1);

      newProcessor.onaudioprocess = (e) => {
        const input = e.inputBuffer.getChannelData(0);
        const buffer = new ArrayBuffer(input.length * 2);
        const view = new DataView(buffer);
        for (let i = 0; i < input.length; i++) {
          const s = Math.max(-1, Math.min(1, input[i]));
          view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        }
        if (socketReadyRef.current && socketRef.current?.connected) {
          socketRef.current.emit("audioData", buffer);
          return;
        }
        if (startCancelledRef.current) return;
        // Socket not ready yet - queue the chunk to be flushed when it is.
        if (pendingAudioBuffersRef.current.length >= MAX_BUFFERED_CHUNKS) {
          pendingAudioBuffersRef.current.shift();
        }
        pendingAudioBuffersRef.current.push(buffer);
      };

      newSource.connect(newProcessor);
      newProcessor.connect(newAudioContext.destination);
      log("audio capture started (buffering until socket ready)");

      setProcessor(newProcessor);
      setSource(newSource);
      setIsRecording(true);
      playMicStartCueA();

      // ----- Socket side: connect, configure, start, then flush queue -----
      connectedSocket = await socketPromise;
      log("socket connected");
      if (startCancelledRef.current) return false;
      if (!connectedSocket || !connectedSocket.connected) {
        throw new Error("Failed to connect to speech recognition server");
      }
      socketRef.current = connectedSocket;

      connectedSocket.emit("setLanguage", { language });
      log(`setLanguage sent: ${language}`);

      const selectedType =
        anathomy && anathomy.trim() !== "" ? anathomy.trim() : null;
      connectedSocket.emit("setType", { type: selectedType });
      log(`setType sent: ${selectedType}`);

      connectedSocket.emit("startAudio");
      log("startAudio sent");

      // Backend has no explicit "ready" ack - once we've sent startAudio we
      // treat the channel as ready and flush. Mark ready BEFORE flushing so
      // any concurrent `onaudioprocess` tick goes straight to the wire
      // (preserving order with the queue we're about to flush).
      socketReadyRef.current = true;
      const queue = pendingAudioBuffersRef.current;
      pendingAudioBuffersRef.current = [];
      log(`flushing ${queue.length} buffered chunk(s)`);
      for (let i = 0; i < queue.length; i++) {
        connectedSocket.emit("audioData", queue[i]);
      }
      log("ready (live)");
      return true;
    } catch (error) {
      console.error("Error starting recording:", error);
      // Best-effort cleanup so we don't leak mic / context / socket.
      try {
        newProcessor?.disconnect();
      } catch (_) {}
      try {
        newSource?.disconnect();
      } catch (_) {}
      try {
        newStream?.getTracks().forEach((t) => t.stop());
      } catch (_) {}
      try {
        newAudioContext?.close();
      } catch (_) {}
      pendingAudioBuffersRef.current = [];
      socketReadyRef.current = false;
      audioContextRef.current = null;
      socketRef.current = null;
      setStream(null);
      setProcessor(null);
      setSource(null);
      setIsRecording(false);
      onError?.(error);
      return false;
    }
  };

  const stopRecording = () => {
    if (isRecording) {
      playMicStopCueA();
    }
    onSessionEnd?.();

    // If we're still mid-startup, signal cancellation so async checkpoints
    // bail out cleanly.
    startCancelledRef.current = true;
    socketReadyRef.current = false;
    pendingAudioBuffersRef.current = [];

    if (socketRef.current?.connected) {
      socketRef.current.emit("endAudio");
    }

    if (processor) processor.disconnect();
    if (source) source.disconnect();
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    disconnectSocket();
    socketRef.current = null;
    setIsRecording(false);
  };

  /** @returns {Promise<boolean>} true when the mic ends up running or was stopped as asked. */
  const toggleRecording = async () => {
    if (!isRecording) {
      return await startRecording();
    }
    stopRecording();
    return true;
  };

  return {
    isRecording,
    toggleRecording,
    startRecording,
    stopRecording,
    socketRef,
  };
};

export { useAudioRecording };
